import ical from 'ical.js';

// Convert iCal event to Aidey task format
export function icalEventToTask(event) {
  try {
    const e = new ical.Event(event);
    
    // ✅ Add fallback for malformed or missing DTSTART/DTEND values
    let startDate, endDate;
    try {
      startDate = e.startDate ? e.startDate.toJSDate() : new Date();
    } catch (startError) {
      console.warn('Invalid start date in iCal event, using current date:', startError);
      startDate = new Date();
    }

    try {
      endDate = e.endDate ? e.endDate.toJSDate() : new Date(startDate.getTime() + 60 * 60 * 1000); // Default 1 hour
    } catch (endError) {
      console.warn('Invalid end date in iCal event, using start date + 1 hour:', endError);
      endDate = new Date(startDate.getTime() + 60 * 60 * 1000);
    }
    
    return {
      id: `ical_${e.uid || Date.now()}`, // Fallback UID if missing
      title: e.summary || "Untitled event",
      date: startDate.toISOString().slice(0, 10),
      time: startDate.toISOString().slice(11, 16),
      deadline: endDate ? endDate.toISOString() : null,
      details: e.description || "",
      priority: "medium", // Default priority for calendar events
      external: true, // Mark as from external calendar
      editable: false, // Don't allow editing in Aidey
      completed: false,
      category: "ical", // Special category for iCal events
      source: "iCal", // Source identifier
      originalEvent: e, // Keep reference to original event
    };
  } catch (error) {
    console.error('Error converting iCal event to task:', error);
    // Return a safe fallback task
    return {
      id: `ical_error_${Date.now()}`,
      title: "Unable to parse event",
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toISOString().slice(11, 16),
      deadline: null,
      details: "Error parsing iCal event",
      priority: "medium",
      external: true,
      editable: false,
      completed: false,
      category: "ical",
      source: "iCal",
      originalEvent: null,
    };
  }
}

// Fetch and parse iCal events from URL
export const fetchICalEvents = async (icalUrl) => {
  try {
    if (!icalUrl || !icalUrl.trim()) {
      console.warn('No iCal URL provided');
      return [];
    }

    console.log('Fetching iCal events from:', icalUrl);
    
    // Use the server proxy to avoid CORS issues
    const proxyUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/ical/proxy?url=${encodeURIComponent(icalUrl.trim())}`;
    
    const response = await fetch(proxyUrl, {
      method: 'GET',
      headers: {
        'Accept': 'text/calendar, application/json',
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      let errorMessage = 'Failed to fetch calendar';
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch {
        errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      }
      
      console.error('Calendar fetch failed:', errorMessage);
      throw new Error(errorMessage);
    }

    const icalText = await response.text();
    
    if (!icalText || !icalText.trim()) {
      console.warn('Empty calendar data received');
      return [];
    }

    console.log('iCal data received, length:', icalText.length);
    console.log('First 100 chars:', icalText.substring(0, 100));

    // ✅ Validate calendar format before parsing (BEGIN:VCALENDAR)
    if (!icalText.includes('BEGIN:VCALENDAR') && !icalText.includes('BEGIN:vcalendar')) {
      console.error('Invalid iCal format - missing VCALENDAR block');
      throw new Error('Invalid calendar format received - not a valid iCal file');
    }

    // ✅ Add guard against jcalData/vevent parsing errors
    let jcalData, comp, vevents;
    try {
      // Parse the iCal data
      jcalData = ical.parse(icalText);
      comp = new ical.Component(jcalData);
      vevents = comp.getAllSubcomponents('vevent');
    } catch (parseError) {
      console.error('Error parsing iCal data:', parseError);
      throw new Error(`Failed to parse calendar data: ${parseError.message || 'Invalid iCal format'}`);
    }

    console.log('Found events:', vevents.length);

    // Filter events to only include recent and upcoming events (6 months past, 12 months future)
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
    const twelveMonthsFromNow = new Date();
    twelveMonthsFromNow.setMonth(twelveMonthsFromNow.getMonth() + 12);

    const events = vevents
      .map((vevent) => {
        try {
          const event = new ical.Event(vevent);
          
          // ✅ Add fallback for malformed or missing DTSTART/DTEND values
          let startDate, endDate;
          try {
            startDate = event.startDate ? event.startDate.toJSDate() : new Date();
          } catch (startError) {
            console.warn('Invalid start date in vevent, using current date:', startError);
            startDate = new Date();
          }

          try {
            endDate = event.endDate ? event.endDate.toJSDate() : new Date(startDate.getTime() + 60 * 60 * 1000);
          } catch (endError) {
            console.warn('Invalid end date in vevent, using start date + 1 hour:', endError);
            endDate = new Date(startDate.getTime() + 60 * 60 * 1000);
          }

          return {
            id: event.uid || `ical-${Date.now()}-${Math.random()}`,
            title: event.summary || 'Untitled Event',
            start: startDate,
            end: endDate,
            description: event.description || '',
            location: event.location || '',
            source: 'ical',
            allDay: event.isRecurring() ? false : (endDate - startDate >= 24 * 60 * 60 * 1000),
            date: startDate.toISOString().slice(0, 10),
            time: startDate.toISOString().slice(11, 16),
          };
        } catch (error) {
          console.warn('Failed to parse event, creating fallback event:', error);
          // ✅ Enhanced fallback for unparseable events
          const fallbackDate = new Date();
          return {
            id: `ical-error-${Date.now()}-${Math.random()}`,
            title: 'Unable to parse event',
            start: fallbackDate,
            end: new Date(fallbackDate.getTime() + 60 * 60 * 1000),
            description: `Parsing error: ${error.message || 'Unknown error'}`,
            location: '',
            source: 'ical',
            allDay: false,
            date: fallbackDate.toISOString().slice(0, 10),
            time: fallbackDate.toISOString().slice(11, 16),
          };
        }
      })
      .filter(event => {
        // ✅ Enhanced validation: Remove null events and filter by date range
        if (!event || !event.start || !event.date) {
          console.warn('Filtering out invalid event:', event);
          return false;
        }
        
        const eventDate = event.start;
        if (!(eventDate instanceof Date) || isNaN(eventDate.getTime())) {
          console.warn('Filtering out event with invalid date:', event);
          return false;
        }
        
        return eventDate >= sixMonthsAgo && eventDate <= twelveMonthsFromNow;
      })
      .slice(0, 1000); // Limit to 1000 events for performance

    console.log('Parsed iCal events:', events.length);
    return events;
  } catch (error) {
    console.error('Error fetching iCal events:', error);
    
    // Provide user-friendly error messages
    if (error.message.includes('Calendar not found')) {
      throw new Error('Calendar not found. Please check that the URL is correct and the calendar is publicly accessible.');
    } else if (error.message.includes('Access denied')) {
      throw new Error('Access denied. This calendar may be private or require authentication.');
    } else if (error.message.includes('timeout')) {
      throw new Error('The calendar server is taking too long to respond. Please try again later.');
    } else if (error.message.includes('Invalid calendar format')) {
      throw new Error('The URL does not contain valid calendar data. Please check the URL.');
    } else {
      throw new Error(`Calendar error: ${error.message}`);
    }
  }
};

// Get all tasks including iCal events for a specific date
export function getAllTasksWithICal(aideyTasks, googleEvents, icalEvents, dateStr) {
  // Aidey tasks for this date
  const localTasks = aideyTasks.filter(t => t.date === dateStr);

  // Google events for this date (using existing function)
  const { calendarEventToTask } = require('./calendarUtils');
  const googleTasks = (googleEvents || [])
    .filter(ev => {
      if (!ev.start) return false;
      const eventDate = ev.start.dateTime
        ? ev.start.dateTime.slice(0, 10)
        : ev.start.date;
      return eventDate === dateStr;
    })
    .map(calendarEventToTask);

  // iCal events for this date
  const icalTasks = (icalEvents || []).filter(task => task.date === dateStr);

  // Combine all tasks and sort by time
  const allTasks = [...localTasks, ...googleTasks, ...icalTasks];
  
  return allTasks.sort((a, b) => {
    if (a.time && b.time) {
      return a.time.localeCompare(b.time);
    }
    if (a.time && !b.time) return -1;
    if (!a.time && b.time) return 1;
    return 0;
  });
}

// Check if a task is from iCal
export function isICalTask(task) {
  return task.category === "ical" || task.id?.startsWith('ical_') || task.source === "iCal";
}

// Validate iCal URL format
export function validateICalUrl(url) {
  if (!url || !url.trim()) {
    return { valid: false, error: 'URL is required' };
  }

  try {
    const trimmedUrl = url.trim();
    
    // Handle webcal:// protocol by converting to https:// for validation
    let urlForValidation = trimmedUrl;
    if (trimmedUrl.startsWith('webcal://')) {
      urlForValidation = trimmedUrl.replace('webcal://', 'https://');
    }
    
    // Parse URL to validate format (this will throw if invalid)
    new URL(urlForValidation);
    
    // Check if it's a valid HTTP/HTTPS/WEBCAL URL
    const originalProtocol = trimmedUrl.split('://')[0] + ':';
    if (!['http:', 'https:', 'webcal:'].includes(originalProtocol)) {
      return { valid: false, error: 'URL must start with http://, https://, or webcal://' };
    }

    // Check for common iCal URL patterns
    const urlLower = trimmedUrl.toLowerCase();
    const hasCalendarIndicators = 
      urlLower.includes('calendar') ||
      urlLower.includes('ical') ||
      urlLower.includes('.ics') ||
      urlLower.includes('webcal') ||
      urlLower.includes('events') ||
      urlLower.includes('caldav') ||
      urlLower.includes('icloud');

    if (!hasCalendarIndicators) {
      return { 
        valid: true, 
        warning: 'This URL doesn\'t look like a typical calendar URL. Make sure it\'s an iCal feed.' 
      };
    }

    return { valid: true };
  } catch (error) {
    return { valid: false, error: 'Invalid URL format' };
  }
}
